import React, { useEffect } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';

import {
  ApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskStatus,
  Application,
  IApplicantOfferRequirementTask,
} from 'types';

import { Note, NoteType } from './Note';
import FormattedFulfilmentData from './FormattedFulfilmentData';
import { getFulfilmentProp } from '@/utilities/fulfilmentData';
import { EditTextFieldModal } from './modals/EditTextFieldModal';
import { useTranslation } from "react-i18next";

type Props = {
  data: IApplicantOfferRequirementTask['conditionHistory'][number];
  openFile: (urls: string[]) => void;
  index: number;
  handleStatusUpdate: (status: ApplicantOfferRequirementTaskStatus, noteText?: string, conditionText?: string) => void;
  onEditCancel: () => void;
  status: ApplicantOfferRequirementTaskStatus;
  conditionText: string;
  initialEditMode?: boolean;
  disableEdit?: boolean;
  requirementId: string;
  taskType: ApplicantOfferRequirementTask;
  application: Application;
  taskText?: string;
  isMultiQualification?: boolean;
};

export default function PreviousCondition({
  data,
  openFile,
  index,
  handleStatusUpdate,
  onEditCancel,
  status,
  conditionText,
  requirementId,
  taskType,
  application,
  taskText,
  isMultiQualification,
  initialEditMode = false,
  disableEdit = false,
}: Props): React.ReactElement {
    const { t } = useTranslation('common');
  const { isOpen: isNoteModalOpen, onOpen: openNoteModal, onClose: closeNoteModal } = useDisclosure();

  useEffect(() => {
    if (initialEditMode) {
      openNoteModal();
    }
  }, [initialEditMode, openNoteModal]);

  const { fulfilmentData } = data;
  // Hide the note row only when there is no saved text AND the row is not editable.
  // When editable with no existing note, show the row so the user can click the edit icon to add one.
  const hideNote = !data.internalNote && disableEdit;

  // Check if there's any fulfilment data (urls, englishTest, text, studyHistory, etc.)
  const hasFulfilmentData =
    fulfilmentData &&
    (getFulfilmentProp(fulfilmentData, 'urls') ||
      getFulfilmentProp(fulfilmentData, 'englishTest') ||
      getFulfilmentProp(fulfilmentData, 'text') ||
      getFulfilmentProp(fulfilmentData, 's3Keys') ||
      getFulfilmentProp(fulfilmentData, 'studyHistory') ||
      getFulfilmentProp(fulfilmentData, 'referees'));

  const handleModalClose = (): void => {
    closeNoteModal();
    if (initialEditMode) {
      onEditCancel();
    }
  };

  const handleModalSave = (noteValue: string): void => {
    handleStatusUpdate(status, noteValue, conditionText);
    closeNoteModal();
  };

  return (
    <Box ml={4} fontSize={'md'} lineHeight={5}>
      {hasFulfilmentData ? (
        <FormattedFulfilmentData
          fulfilmentData={fulfilmentData}
          preview={openFile}
          requirementId={requirementId}
          taskType={taskType}
          application={application}
          taskText={taskText}
          isMultiQualification={isMultiQualification}
        />
      ) : (
        <Box color="muted">{t('common:linkToDocumentNotAvailable')}</Box>
      )}

      <Flex mt={2}>
        {!hideNote && (
          <Note
            status={NoteType.InternalNote}
            noteText={data.internalNote ?? ''}
            onEdit={openNoteModal}
            id={NoteType.InternalNote}
            index={index}
            disableEdit={disableEdit}
          />
        )}
      </Flex>

      <EditTextFieldModal
        isOpen={isNoteModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        title={data.internalNote ? t('common:editInternalNote') : t('common:addInternalNote')}
        fieldLabel={data.internalNote ? 'Internal note' : 'Internal note (optional)'}
        fieldSubLabel="Not shared with the applicant"
        placeholder={t('common:addNote')}
        initialValue={data.internalNote ?? ''}
      />
    </Box>
  );
}
